(() => {
  if (window.LisaGiftCoupon) return;
  const source = document.getElementById('LisaGiftCouponConfig');
  if (!source) return;
  const config = JSON.parse(source.textContent);
  const root = window.Shopify?.routes?.root || '/';
  const panels = new Set();
  const legacy = { 'Complimentary fragrance': '', 'Fragrance request': '', 'Fragrance fallback': '' };
  let cart;
  let queue = Promise.resolve();
  let pending = 0;
  let refreshing;
  const giftLines = value => (value?.items || []).filter(item => item.properties?._lisa_gift === config.code);
  const codes = value => [...new Set((value?.discount_codes || []).map(item => item.code).filter(Boolean))];
  async function request(path, body) {
    const response = await fetch(root + path, {
      method: body ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store',
      headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) },
      ...(body ? { body: JSON.stringify(body) } : {})
    });
    if (!response.ok) throw new Error(config.labels.error);
    return response.json();
  }
  function render() { panels.forEach(panel => panel.render()); }
  function enqueue(work) {
    pending++;
    document.documentElement.classList.add('lisa-gift-saving');
    render();
    const task = queue.then(work);
    queue = task.catch(() => {}).finally(() => {
      pending--;
      document.documentElement.classList.toggle('lisa-gift-saving', pending > 0);
      render();
    });
    return task;
  }
  function targets() {
    const result = [{ id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' }];
    for (const id of ['main-cart-items', 'main-cart-footer']) {
      const element = document.getElementById(id);
      if (element?.dataset.id) result.push({ id, section: element.dataset.id, selector: '.js-contents' });
    }
    return result;
  }
  function updateView(value, list) {
    cart = value;
    list.forEach(target => {
      const html = value.sections?.[target.section];
      if (!html) return;
      const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector(target.selector);
      const element = document.getElementById(target.id);
      if (fresh && element) (element.querySelector(target.selector) || element).innerHTML = fresh.innerHTML;
    });
    document.querySelector('cart-items')?.classList.toggle('is-empty', value.item_count === 0);
    document.getElementById('main-cart-footer')?.classList.toggle('is-empty', value.item_count === 0);
    const items = document.querySelector('cart-items');
    if (items) items.currentItemCount = value.item_count;
    render();
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: value, source: 'gift-coupon' } }));
  }
  async function finish(value, includeCode) {
    const retained = codes(value).filter(code => code.toUpperCase() !== config.code);
    if (includeCode) retained.push(config.code);
    const list = targets();
    const result = await request('cart/update.js', {
      attributes: legacy, discount: retained.join(','),
      sections: [...new Set(list.map(target => target.section))], sections_url: location.pathname
    });
    updateView(result, list);
    return result;
  }
  async function change(choice, fallback = '6 PM Royal', fragranceRequest = '', claiming = false) {
    let value = await request('cart.js');
    let existing = giftLines(value);
    if (claiming && existing.length) {
      return finish(value, true);
    }
    if (choice === 'No gift') {
      for (const line of existing) value = await request('cart/change.js', { id: line.key, quantity: 0 });
      return finish(value, false);
    }
    const selected = choice === 'Other' ? fallback : choice;
    const variant = config.variants[selected];
    if (!variant?.id || !variant.available) throw new Error(config.labels.unavailable);
    const properties = { _lisa_gift: config.code, _lisa_gift_choice: choice };
    if (choice === 'Other') {
      properties['Fragrance request'] = fragranceRequest.trim().slice(0, 120);
      properties['Fragrance fallback'] = selected;
    }
    const matching = existing.find(line => String(line.variant_id) === String(variant.id));
    let added = false;
    if (matching) {
      value = await request('cart/change.js', { id: matching.key, quantity: 1, properties });
    } else {
      await request('cart/add.js', { items: [{ id: variant.id, quantity: 1, properties }] });
      added = true;
      value = await request('cart.js');
    }
    try {
      for (const line of giftLines(value).filter(line => String(line.variant_id) !== String(variant.id))) {
        value = await request('cart/change.js', { id: line.key, quantity: 0 });
      }
      return await finish(value, true);
    } catch (error) {
      if (added) {
        try {
          const latest = await request('cart.js');
          const addedLine = giftLines(latest).find(line => String(line.variant_id) === String(variant.id));
          if (addedLine) await request('cart/change.js', { id: addedLine.key, quantity: 0 });
        } catch {}
      }
      throw error;
    }
  }
  async function refresh() {
    if (refreshing) return refreshing;
    refreshing = (async () => {
      await queue;
      let value = await request('cart.js');
      if (Object.keys(legacy).some(key => value.attributes?.[key])) {
        value = await request('cart/update.js', { attributes: legacy });
      }
      cart = value;
      render();
    })().finally(() => { refreshing = null; });
    return refreshing;
  }
  window.LisaGiftCoupon = {
    labels: config.labels,
    apply: () => enqueue(async () => finish(await request('cart.js'), true)),
    claim: () => enqueue(() => change('6 PM Royal', '6 PM Royal', '', true)),
    update: (choice, fallback, text) => enqueue(() => change(choice, fallback, text)),
    refresh
  };
  class GiftChoice extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      panels.add(this);
      this.hidden = false;
      this.querySelector('[data-gift-remove]').addEventListener('click', () => this.save('No gift'));
      this.querySelector('[data-gift-undo]').addEventListener('click', () => this.save('6 PM Royal'));
      this.render();
      refresh().catch(() => this.message(config.labels.error));
    }
    disconnectedCallback() { panels.delete(this); }
    message(text) { this.querySelector('[data-gift-status]').textContent = text; }
    async save(choice) {
      this.message(config.labels.adding);
      try {
        await window.LisaGiftCoupon.update(choice);
        this.message(choice === 'No gift' ? config.labels.removed : config.labels.saved);
      } catch (error) { this.message(error.message || config.labels.error); }
    }
    render() {
      const line = giftLines(cart)[0];
      this.querySelector('[data-gift-controls]').hidden = !line;
      this.querySelector('[data-gift-undo]').hidden = !!line;
      this.querySelectorAll('button, select, input').forEach(input => { input.disabled = pending > 0 || !cart; });
      this.querySelector('[data-gift-summary]').textContent = !line ? config.labels.empty : line.final_line_price === 0 ? config.labels.free : config.labels.pending;
    }
  }
  if (!customElements.get('lisa-gift-choice')) customElements.define('lisa-gift-choice', GiftChoice);
  document.addEventListener('cart:updated', event => {
    if (event.detail?.source !== 'gift-coupon' && panels.size) refresh().catch(() => {});
  });
  document.addEventListener('submit', event => {
    if (pending && event.target.id === 'cart') {
      event.preventDefault();
      event.stopImmediatePropagation();
      panels.forEach(panel => panel.message(config.labels.adding));
    }
  }, true);
})();

